import { Paperclip } from "lucide-react";
import { motion } from "motion/react";
import { memo, useMemo } from "react";

import { Card, CardContent, CardHeader } from "@/components/ui/card";

import type { ChatMessage } from "@/lib/ai-builder/types";
import { formatFileSize } from "@/lib/ai-builder/utils";
import { ExpandableJsonBlock } from "./expandable-json-block";

interface ChatMessageItemProps {
	message: ChatMessage;
	index: number;
}

export const ChatMessageItem = memo(function ChatMessageItem({
	message,
	index,
}: ChatMessageItemProps) {
	const contentLines = useMemo(() => {
		if (message.role === "user") {
			return message.content.split("\n");
		}
		return null;
	}, [message.content, message.role]);

	return (
		<motion.div
			animate={{ opacity: 1, y: 0 }}
			className={`flex gap-3 ${
				message.role === "user" ? "justify-end" : "justify-start"
			}`}
			exit={{ opacity: 0, y: -20 }}
			initial={{ opacity: 0, y: 20 }}
			key={index}
		>
			<Card
				className={`max-w-[90%] gap-2 p-2 shadow-none ${
					message.role === "user"
						? "bg-primary text-primary-foreground"
						: "bg-muted/50"
				}`}
			>
				<CardContent className="p-1">
					{message.role === "assistant" && (
						<CardHeader className="px-0">
							<div className="flex items-center gap-1">
								<div className="size-2 rounded-2xl bg-muted-foreground" />
								<p className="font-medium text-xs">AI</p>
							</div>
						</CardHeader>
					)}
					<div className="flex flex-col gap-2">
						{message.role === "user" && message.attachment && (
							<div className="flex items-center gap-1.5 self-end rounded-md bg-primary-foreground/15 px-2 py-1 text-xs">
								<Paperclip aria-hidden="true" className="size-3 shrink-0" />
								<span className="max-w-56 truncate">
									{message.attachment.name}
								</span>
								<span className="opacity-70">
									{formatFileSize(message.attachment.size)}
								</span>
							</div>
						)}
						<div className="prose prose-sm max-w-none">
							{message.role === "user" && contentLines && (
								<div>
									{contentLines.map((line: string, i: number) => (
										<p className="text-sm" key={i}>
											{line}
										</p>
									))}
								</div>
							)}
							{message.role === "assistant" && message.schema && (
								<ExpandableJsonBlock schema={message.schema} />
							)}
							{message.role === "assistant" && !message.schema && (
								<div className="whitespace-pre-wrap break-words text-sm">
									{message.content}
								</div>
							)}
						</div>
					</div>
				</CardContent>
			</Card>
		</motion.div>
	);
});
